'use client';
import { useState, useEffect } from 'react';
import { estNumeroTvaBelgeValide } from '@/app/lib/bce-format';

// ⚠️ 10/09/2026 — « on a pas de guide, on reçoit la demande du client et on
// lui répond par mail. » Ce popup n'envoie plus de guide automatique (aucun
// n'a jamais existé pour de vrai — cf. l'historique de guide-gratuit-mail.js,
// supprimé dans ce même commit) : il capture une demande de contact, l'équipe
// répond ensuite par email, à la main.
//
// ⚠️ TRADUCTION LOCALE, PAS useLang()/tx() — et c'est délibéré. Ce popup vit
// sur la vitrine PUBLIQUE (`app/vitrine/page.js`), qui n'est PAS enveloppée
// dans un <LangProvider> (celui-ci ne couvre que le dashboard connecté, cf.
// app/(dashboard)/layout-client.js). Un useLang() ici retombe donc sur la
// valeur PAR DÉFAUT du contexte — toujours 'fr', tx() = identité — quoi que
// le visiteur choisisse sur /nl. Constaté le 10/09/2026 : le popup restait en
// français même une fois le site basculé en néerlandais. La vitrine porte
// déjà sa propre langue en état local (`const [lang,setLang]=useState(...)`)
// et la transmet en PROP à chaque sous-composant (PageComp, CookieBanner…) —
// ce popup suit maintenant le même patron, avec son propre petit dictionnaire
// FR/NL local plutôt que le contexte global.
const T = {
  fr: {
    badge: 'Contactez-nous',
    icone: 'Nous contacter',
    titre1: 'La paie belge,', titre2: 'sans y passer vos soirées',
    description: 'Décrivez-nous votre besoin, notre équipe vous répond par email.',
    puces: ['✅ Paie & fiches', '✅ Dimona & DmfA', '✅ Contrats'],
    prenom: 'Prénom',
    nom: 'Nom',
    email: 'Votre email professionnel',
    tel: 'Votre numéro de téléphone',
    tva: 'Numéro de TVA belge (ex. BE0123456749)',
    tvaInvalide: 'Numéro de TVA belge invalide.',
    message: 'Expliquez-nous votre demande',
    envoi: 'Envoi...',
    envoyer: 'Envoyer ma demande →',
    footer: 'Vos données restent confidentielles, conformément au RGPD.',
    succesTitre: 'Message envoyé !',
    succesTexte: 'Nous avons bien reçu votre demande. Notre équipe vous répond par email dans les meilleurs délais.',
    fermer: 'Fermer',
  },
  nl: {
    badge: 'Contacteer ons',
    icone: 'Contacteer ons',
    titre1: 'De Belgische loonberekening,', titre2: 'zonder er uw avonden aan te besteden',
    description: 'Beschrijf ons uw vraag, ons team antwoordt u per e-mail.',
    puces: ['✅ Loon & fiches', '✅ Dimona & DmfA', '✅ Contracten'],
    prenom: 'Voornaam',
    nom: 'Naam',
    email: 'Uw professioneel e-mailadres',
    tel: 'Uw telefoonnummer',
    tva: 'Belgisch btw-nummer (bv. BE0123456749)',
    tvaInvalide: 'Ongeldig Belgisch btw-nummer.',
    message: 'Leg ons uw vraag uit',
    envoi: 'Verzenden...',
    envoyer: 'Verstuur mijn aanvraag →',
    footer: 'Uw gegevens blijven vertrouwelijk, conform de AVG.',
    succesTitre: 'Bericht verzonden!',
    succesTexte: 'We hebben uw aanvraag goed ontvangen. Ons team antwoordt u per e-mail zo snel mogelijk.',
    fermer: 'Sluiten',
  },
};

export default function PopupLeadMagnet({ lang = 'fr' }) {
  const t = T[lang] || T.fr;
  const [visible, setVisible] = useState(false);
  const [prenom, setPrenom] = useState('');
  const [nom, setNom] = useState('');
  const [email, setEmail] = useState('');
  const [tel, setTel] = useState('');
  const [tva, setTva] = useState('');
  const [message, setMessage] = useState('');
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  // Tant que nom, prénom, email, téléphone, un numéro de TVA belge VALIDE et
  // le message ne sont pas tous remplis, l'envoi ne part pas — demande
  // fondateur du 10/09/2026 : un lead qu'on ne peut ni identifier ni rappeler
  // n'en est pas un (même règle que les formulaires « Nous contacter » et
  // Nexus, étendue au numéro de TVA, avec sa clé de contrôle MOD 97 — cf.
  // bce-format.js). Le message, d'abord facultatif, est devenu obligatoire le
  // même jour : le fondateur veut savoir CE QUE le prospect demande avant de
  // le rappeler, pas seulement pouvoir le rappeler.
  // Le contrôle serveur, dans /api/lead-capture, est le seul qui tienne
  // réellement (cf. __tests__/telephone-obligatoire.test.js) ; celui-ci
  // n'est qu'un confort.
  const tvaValide = estNumeroTvaBelgeValide(tva);
  const complet = !!(prenom.trim() && nom.trim() && email.trim() && tel.trim() && tvaValide && message.trim());

  useEffect(() => {
    // Demande fondateur du 11/09/2026, en deux temps le même jour : d'abord
    // « il doit apparaître directement » (l'icône, sans attendre 15 s), puis
    // « je voudrais que l'icône s'ouvre à l'ouverture du site » — le popup
    // COMPLET s'ouvre donc maintenant DÈS LE CHARGEMENT de la page, sans
    // minuteur. Un visiteur qui l'a déjà vu et fermé (`aureus_popup_seen`)
    // n'est pas re-sollicité : il retrouve directement l'icône (cf. le rendu
    // plus bas), qui reste le moyen d'y revenir à tout moment.
    const seen = localStorage.getItem('aureus_popup_seen');
    if (seen) return;
    setVisible(true);
  }, []);

  const close = () => {
    setVisible(false);
    localStorage.setItem('aureus_popup_seen', '1');
  };

  const submit = async () => {
    if (!complet) return;
    setLoading(true);
    try {
      await fetch('/api/lead-capture', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ prenom, nom, email, tel, tva, message, source: 'popup_contact', lang }),
      });
      setSent(true);
      localStorage.setItem('aureus_popup_seen', '1');
    } catch { setSent(true); }
    setLoading(false);
  };

  if (!visible) {
    // L'icône de réouverture : bouton fixé en bas à droite, affiché DÈS LE
    // CHARGEMENT de la page (pas seulement après une fermeture) — tant que
    // le popup complet n'est pas ouvert, l'icône reste le moyen d'y accéder
    // à tout moment. Demande fondateur du 11/09/2026 : le libellé « Nous
    // contacter » s'affiche à côté de l'icône, pas seulement en aria-label —
    // un rond seul ne se repère pas assez vite sur une page longue.
    return (
      <button
        onClick={() => setVisible(true)}
        aria-label={t.icone}
        title={t.icone}
        style={{
          position: 'fixed', bottom: 24, right: 24, zIndex: 9998,
          display: 'flex', alignItems: 'center', gap: 10,
          padding: '14px 20px 14px 16px', borderRadius: 999,
          background: '#1a1a18', color: '#c6a34e', border: 'none',
          boxShadow: '0 8px 24px rgba(0,0,0,0.3)', cursor: 'pointer',
          fontFamily: 'system-ui, sans-serif',
        }}
      >
        <span style={{ fontSize: 22, lineHeight: 1 }}>💬</span>
        <span style={{ fontSize: 15, fontWeight: 700 }}>{t.icone}</span>
      </button>
    );
  }

  const inputStyle = {
    width: '100%', padding: '14px 16px', fontSize: 15,
    border: '1.5px solid #e0ddd5', borderRadius: 10,
    outline: 'none', marginBottom: 12, boxSizing: 'border-box',
    fontFamily: 'system-ui, sans-serif',
  };
  const onEnter = e => e.key === 'Enter' && submit();

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)',
      zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: 24, fontFamily: 'system-ui, sans-serif',
    }}>
      <div style={{
        background: '#fff', borderRadius: 20, maxWidth: 480, width: '100%',
        padding: 40, position: 'relative', boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
      }}>
        {/* Bouton fermer */}
        <button onClick={close} aria-label={t.fermer} style={{
          position: 'absolute', top: 16, right: 16, background: 'none',
          border: 'none', fontSize: 22, cursor: 'pointer', color: '#999', lineHeight: 1,
        }}>×</button>

        {sent ? (
          <div style={{ textAlign: 'center', padding: '20px 0' }}>
            <div style={{ fontSize: 48, marginBottom: 16 }}>🎉</div>
            <h3 style={{ fontSize: 22, fontWeight: 800, color: '#1a1a18', marginBottom: 8 }}>
              {t.succesTitre}
            </h3>
            <p style={{ fontSize: 15, color: '#666', lineHeight: 1.7 }}>
              {t.succesTexte}
            </p>
            <button onClick={close} style={{
              marginTop: 20, background: '#1a1a18', color: '#c6a34e',
              border: 'none', borderRadius: 8, padding: '12px 28px',
              fontWeight: 700, cursor: 'pointer', fontSize: 15,
            }}>{t.fermer}</button>
          </div>
        ) : (
          <>
            <div style={{
              background: '#c6a34e18', borderRadius: 10, padding: '8px 14px',
              fontSize: 11, fontWeight: 700, color: '#c6a34e',
              textTransform: 'uppercase', letterSpacing: 2, display: 'inline-block', marginBottom: 16,
            }}>{t.badge}</div>

            <h2 style={{ fontSize: 24, fontWeight: 900, color: '#1a1a18', margin: '0 0 12px', lineHeight: 1.3 }}>
              {t.titre1} <span style={{ color: '#c6a34e' }}>{t.titre2}</span>
            </h2>

            <p style={{ fontSize: 14, color: '#666', lineHeight: 1.7, marginBottom: 24 }}>
              {t.description}
            </p>

            <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
              {t.puces.map(item => (
                <span key={item} style={{
                  background: '#eef7ee', color: '#16a34a', fontSize: 11,
                  fontWeight: 600, padding: '4px 10px', borderRadius: 20,
                }}>{item}</span>
              ))}
            </div>

            <div style={{ display: 'flex', gap: 8 }}>
              <input
                type="text"
                placeholder={t.prenom}
                value={prenom}
                onChange={e => setPrenom(e.target.value)}
                onKeyDown={onEnter}
                style={inputStyle}
              />
              <input
                type="text"
                placeholder={t.nom}
                value={nom}
                onChange={e => setNom(e.target.value)}
                onKeyDown={onEnter}
                style={inputStyle}
              />
            </div>
            <input
              type="email"
              placeholder={t.email}
              value={email}
              onChange={e => setEmail(e.target.value)}
              onKeyDown={onEnter}
              style={inputStyle}
            />
            <input
              type="tel"
              placeholder={t.tel}
              value={tel}
              onChange={e => setTel(e.target.value)}
              onKeyDown={onEnter}
              style={inputStyle}
            />
            <input
              type="text"
              placeholder={t.tva}
              value={tva}
              onChange={e => setTva(e.target.value)}
              onKeyDown={onEnter}
              style={{
                ...inputStyle,
                borderColor: tva.trim() && !tvaValide ? '#dc2626' : '#e0ddd5',
              }}
            />
            {tva.trim() && !tvaValide && (
              <p style={{ fontSize: 12, color: '#dc2626', margin: '-6px 0 12px' }}>
                {t.tvaInvalide}
              </p>
            )}
            <textarea
              placeholder={t.message}
              value={message}
              onChange={e => setMessage(e.target.value)}
              rows={3}
              style={{
                ...inputStyle,
                resize: 'vertical',
                fontFamily: 'system-ui, sans-serif',
              }}
            />

            <button
              onClick={submit}
              disabled={loading || !complet}
              style={{
                width: '100%', background: complet ? '#1a1a18' : '#ccc',
                color: complet ? '#c6a34e' : '#fff', border: 'none',
                borderRadius: 10, padding: '15px', fontWeight: 800,
                fontSize: 16, cursor: complet ? 'pointer' : 'not-allowed',
              }}
            >
              {loading ? t.envoi : t.envoyer}
            </button>

            <p style={{ fontSize: 11, color: '#aaa', textAlign: 'center', marginTop: 12 }}>
              {t.footer}
            </p>
          </>
        )}
      </div>
    </div>
  );
}
